import browser from "webextension-polyfill";
import { GET_DIALOG_MESSAGE_TYPE, DIALOG_ACTION_MESSAGE_TYPE, DISMISS_DIALOG_MESSAGE_TYPE, } from "../shared/constants.js";
function isDarkMode() {
    const docAttr = document.documentElement.getAttribute("dark");
    const ytdApp = document.querySelector("ytd-app");
    return docAttr !== null || (ytdApp === null || ytdApp === void 0 ? void 0 : ytdApp.hasAttribute("dark")) === true;
}
let dialogContainer = null;
const DIALOG_STYLES = `
  :host {
    position: fixed;
    top: 37px;
    right: 37px;
    z-index: 2147483647;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
  }

  #dialog {
    position: relative;
    border-radius: 12px;
    padding: 20px 24px;
    width: 300px;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 13px;
  }

  #header {
    display: flex;
    align-items: center;
    margin-bottom: 14px;
    gap: 8px;
  }

  #logo {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  #title {
    font-size: 14px;
    font-weight: 700;
    flex: 1;
    line-height: 1.3;
  }

  #close-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;
  }

  #cta-btn {
    display: block;
    width: auto;
    margin-top: 18px;
    margin-left: auto;
    margin-right: auto;
    padding: 10px 40px;
    background: #e53935;
    color: #fff;
    border: none;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    font-family: Arial, Helvetica, sans-serif;
  }

  #cta-btn:hover {
    background: #c62828;
  }

  #learn-more {
    display: block;
    text-align: center;
    margin-top: 10px;
    font-size: 13px;
    text-decoration: none;
  }

  .heading {
    font-size: 13px;
    font-weight: 600;
    margin: 12px 0 6px 0;
    text-align: left;
  }

  .list-item {
    font-size: 13px;
    line-height: 1.6;
    margin: 0;
    text-align: left;
    padding-left: 8px;
  }

  .check {
    color: #e53935;
    margin-right: 8px;
  }

  .body-text {
    font-size: 13px;
    line-height: 1.5;
    margin: 0 0 8px 0;
    padding: 0 8px;
  }
`;
const DARK_DIALOG_STYLES = `
  #dialog {
    background: #282828;
    color: #e0e0e0;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
  }

  #title {
    color: #fff;
  }

  #close-btn {
    color: #888;
  }

  #close-btn:hover {
    color: #fff;
  }

  #learn-more {
    color: #aaa;
  }

  #learn-more:hover {
    color: #e0e0e0;
  }

  .heading {
    color: #e0e0e0;
  }

  .list-item {
    color: #aaa;
  }

  .body-text {
    color: #aaa;
  }

  strong {
    color: #e0e0e0;
  }
`;
const LIGHT_DIALOG_STYLES = `
  #dialog {
    background: #ffffff;
    color: #333333;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  }

  #title {
    color: #111111;
  }

  #close-btn {
    color: #777777;
  }

  #close-btn:hover {
    color: #111111;
  }

  #learn-more {
    color: #666666;
  }

  #learn-more:hover {
    color: #333333;
  }

  .heading {
    color: #333333;
  }

  .list-item {
    color: #555555;
  }

  .body-text {
    color: #555555;
  }

  strong {
    color: #333333;
  }
`;
function appendRichText(parent, text) {
    const parts = text.split(/(<strong>.*?<\/strong>)/g);
    for (const part of parts) {
        const match = /^<strong>(.*?)<\/strong>$/.exec(part);
        if (match) {
            const strong = document.createElement("strong");
            strong.textContent = match[1];
            parent.appendChild(strong);
        }
        else if (part) {
            parent.appendChild(document.createTextNode(part));
        }
    }
}
function renderBodyLine(line) {
    if (line.style === "heading") {
        const el = document.createElement("p");
        el.className = "heading";
        el.textContent = line.text;
        return el;
    }
    if (line.style === "list-item") {
        const el = document.createElement("p");
        el.className = "list-item";
        const check = document.createElement("span");
        check.className = "check";
        check.textContent = "\u2713";
        el.appendChild(check);
        el.appendChild(document.createTextNode(line.text));
        return el;
    }
    const el = document.createElement("p");
    el.className = "body-text";
    appendRichText(el, line.text);
    return el;
}
function removeDialog() {
    if (dialogContainer) {
        dialogContainer.remove();
        dialogContainer = null;
    }
}
function buildDialog(payload) {
    const host = document.createElement("div");
    const shadow = host.attachShadow({ mode: "closed" });
    const style = document.createElement("style");
    style.textContent =
        DIALOG_STYLES + (isDarkMode() ? DARK_DIALOG_STYLES : LIGHT_DIALOG_STYLES);
    shadow.appendChild(style);
    const dialog = document.createElement("div");
    dialog.id = "dialog";
    const header = document.createElement("div");
    header.id = "header";
    if (payload.logoUrl) {
        const logo = document.createElement("img");
        logo.id = "logo";
        logo.src = payload.logoUrl;
        header.appendChild(logo);
    }
    const title = document.createElement("span");
    title.id = "title";
    title.textContent = payload.title;
    header.appendChild(title);
    dialog.appendChild(header);
    const closeBtn = document.createElement("button");
    closeBtn.id = "close-btn";
    closeBtn.textContent = "\u00d7";
    closeBtn.addEventListener("click", () => {
        void browser.runtime.sendMessage({
            type: DIALOG_ACTION_MESSAGE_TYPE,
            touchPointStep: payload.touchPointStep,
            action: "close",
        });
        removeDialog();
    });
    dialog.appendChild(closeBtn);
    for (const line of payload.body) {
        dialog.appendChild(renderBodyLine(line));
    }
    const ctaBtn = document.createElement("button");
    ctaBtn.id = "cta-btn";
    ctaBtn.textContent = payload.button;
    ctaBtn.addEventListener("click", () => {
        if (payload.target) {
            window.open(payload.target, "_blank");
        }
        void browser.runtime.sendMessage({
            type: DIALOG_ACTION_MESSAGE_TYPE,
            touchPointStep: payload.touchPointStep,
            action: payload.target ? "cta" : "close",
        });
        removeDialog();
    });
    dialog.appendChild(ctaBtn);
    if (payload.learnMore) {
        const learnMore = document.createElement("a");
        learnMore.id = "learn-more";
        learnMore.href = payload.learnMore.url;
        learnMore.target = "_blank";
        learnMore.textContent = payload.learnMore.text;
        learnMore.addEventListener("click", () => {
            void browser.runtime.sendMessage({
                type: DIALOG_ACTION_MESSAGE_TYPE,
                touchPointStep: payload.touchPointStep,
                action: "cta",
            });
        });
        dialog.appendChild(learnMore);
    }
    shadow.appendChild(dialog);
    return host;
}
function renderDialog(payload) {
    if (dialogContainer) {
        return;
    }
    dialogContainer = buildDialog(payload);
    document.body.appendChild(dialogContainer);
}
export async function start() {
    if (dialogContainer) {
        return;
    }
    let payload = null;
    try {
        payload = await browser.runtime.sendMessage({
            type: GET_DIALOG_MESSAGE_TYPE,
        });
    }
    catch (_a) {
        return;
    }
    if (!payload) {
        return;
    }
    const validPayload = payload;
    if (document.body) {
        renderDialog(validPayload);
    }
    else {
        document.addEventListener("DOMContentLoaded", () => {
            renderDialog(validPayload);
        }, { once: true });
    }
    browser.runtime.onMessage.addListener(function dismissListener(message) {
        if ((message === null || message === void 0 ? void 0 : message.type) === DISMISS_DIALOG_MESSAGE_TYPE) {
            removeDialog();
            browser.runtime.onMessage.removeListener(dismissListener);
            return Promise.resolve();
        }
    });
}
